import { useRollbar } from "@rollbar/react";
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { PropsWithChildren } from "react";
import Rollbar from "rollbar";
import { PartialDeep } from "type-fest";
import {
	isExpectedBusinessError,
	isNetworkError,
} from "utils/errorClassification";
import { isObjWithError } from "utils/errorHelpers";
import { getAuthRedirectWithReturn } from "utils/getAuthRedirectWithReturn";
import { getNormalizedErrorMessage } from "utils/getNormalizedErrorMessage";
import {
	trackNetworkFailure,
	trackNetworkRecovery,
} from "utils/networkIncidentReporter";

export function ReactQueryProvider({ children }: PropsWithChildren<{}>) {
	const rollbar = useRollbar();
	const queryClient = getQueryClient(rollbar);
	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}

/** query client init */
export function getQueryClient(rollbar: Rollbar) {
	return new QueryClient({
		defaultOptions: {
			queries: {
				/** we're not worried about stale data when a user doesn't have the app open for most queries */
				refetchOnWindowFocus: false,
				retry: false,
				/** 0 is default, but just declaring it here. All queries don't use caching unless we add a new staleTime to the specific query */
				staleTime: 0,
			},
		},

		logger: {
			/** silence built in console errors for react-query because we handle them ourselves in the handleError function of useSnackbar */
			error: () => void 0,
			log: console.log,
			warn: console.warn,
		},

		mutationCache: new MutationCache({
			onError: (err) => {
				if (isExpectedBusinessError(err)) {
					return;
				}

				const errorMessage = getNormalizedErrorMessage(err);

				rollbar.error(new Error(errorMessage), {
					error: err,
				});
			},
			onSuccess: (data) => {
				if (isObjWithError(data) && data.error != undefined) {
					if (isExpectedBusinessError(data.error)) {
						return;
					}

					const errorMessage = getNormalizedErrorMessage(data.error);

					rollbar.error(new Error(errorMessage), {
						error: data.error,
					});
				}
			},
		}),

		queryCache: new QueryCache({
			onSuccess: () => trackNetworkRecovery(),
			/** correctly ignored: we won't know how the error is coming in */
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onError: (errorAny: any, query) => {
				const error = errorAny as PartialDeep<{
					message: string;
					request: {
						responseURL: string;
					};
					response: {
						errors: {
							message: string;
							path: string[];
						}[];
						status: number;
					};
				}>;

				const mfaRequiredError = error?.response?.errors?.some(
					(error) => error.message === "mfa_required"
				);

				if (mfaRequiredError) {
					// If already on an auth route, don't redirect to auth route
					if (window.location.pathname.includes("authenticate")) {
						window.location.replace("/authenticate");
					} else {
						window.location.replace(getAuthRedirectWithReturn("/authenticate"));
					}
				} else if (isUnauthenticatedError(errorAny)) {
					if (!isAuthRoute()) {
						getQueryClient(rollbar).clear();
						window.location.replace(getAuthRedirectWithReturn("/signin"));
					}
				} else if (isNetworkError(errorAny)) {
					trackNetworkFailure(rollbar, query.queryKey);
				} else {
					rollbar.error(new Error(errorAny), () => {
						if (error.message?.includes("missing context.user_id")) {
							window.location.replace("/unauthorized");
						}
					});
				}
			},
		}),
	});
}

function isAuthRoute(): boolean {
	/**
	 * If the getPerson query fails on one of these routes,
	 * that's okay because there shouldn't be a person yet
	 */
	if (
		window.location.pathname === "/" ||
		window.location.pathname.includes("signin") ||
		window.location.pathname.includes("signup") ||
		window.location.pathname.includes("reset_password") ||
		window.location.pathname.includes("change_email") ||
		window.location.pathname.includes("unauthorized") ||
		window.location.pathname.includes("irn")
	) {
		return true;
	}
	return false;
}

function isUnauthenticatedError(error: unknown): boolean {
	if (
		error &&
		typeof error === "object" &&
		"response" in error &&
		error.response &&
		typeof error.response === "object"
	) {
		if (
			"errors" in error.response &&
			error.response.errors &&
			error.response.errors instanceof Array
		) {
			return error.response.errors.some(
				(err) =>
					typeof err === "object" &&
					"message" in err &&
					err.message === "unauthenticated"
			);
		}

		if ("status" in error.response && error?.response?.status === 401) {
			return true;
		}
	}

	return false;
}
